<template>
  <div class="history-invoices-tab">
    <!-- Filtros (Competência + Search Input alinhados na mesma linha) -->
    <div class="filters-container mb-3">
      <!-- Filtro Competência com Vue Monthly Picker -->
      <div class="form-select-group">
        <label class="select-label">Competência</label>
        <vue-monthly-picker
          class="custom-monthly-picker"
          v-model="selectedCompetence"
          :monthLabels="monthLabels"
          dateFormat="MM/YYYY"
          :max="maxDate"
          @input="onFilterChange"
        ></vue-monthly-picker>
      </div>

      <!-- Filtro de Busca por NF -->
      <div class="form-search-group">
        <b-input-group class="custom-search-group">
          <b-input-group-prepend is-text class="search-icon-prepend">
            <i class="fas fa-search search-icon"></i>
          </b-input-group-prepend>
          <b-form-input
            v-model="invoiceSearch"
            placeholder="Busque por NF"
            class="custom-search-input"
            @input="onSearchInput"
          ></b-form-input>
        </b-input-group>
      </div>
    </div>

    <!-- Tabela de Notas Enviadas -->
    <div class="table-responsive history-table-wrapper">
      <b-table
        :items="filteredItems"
        :fields="fields"
        responsive
        show-empty
        empty-text="Nenhuma nota fiscal encontrada!"
        class="custom-history-table"
      >
        <!-- Coluna Competência -->
        <template #cell(competence)="data">
          <span class="cell-text">{{ data.item.competence }}</span>
        </template>

        <!-- Coluna Número da Nota -->
        <template #cell(invoiceNumber)="data">
          <span class="cell-text font-weight-bold">{{ data.item.invoiceNumber }}</span>
        </template>

        <!-- Coluna Data de Integração -->
        <template #cell(integrationDate)="data">
          <span class="cell-text">{{ data.item.integrationDate }}</span>
        </template>

        <!-- Coluna Ações -->
        <template #cell(actions)="data">
          <div class="d-flex align-items-center justify-content-end gap-2">
            <!-- Botão Excluir (Ativo) -->
            <b-button
              v-if="data.item.canDelete"
              variant="outline-danger"
              class="btn-delete-active mr-2"
              @click="$emit('delete', data.item)"
              title="Excluir Nota Fiscal"
            >
              <i class="fas fa-trash-alt"></i>
            </b-button>

            <!-- Botão Excluir (Desabilitado com Tooltip) -->
            <div v-else class="d-inline-block mr-2" :id="'disabled-del-' + data.index">
              <b-button variant="outline-secondary" class="btn-delete-disabled" disabled>
                <i class="fas fa-trash-alt"></i>
              </b-button>
              <b-tooltip :target="'disabled-del-' + data.index" placement="top">
                Nota processada com sucesso. O período de exclusão já foi encerrado.
              </b-tooltip>
            </div>

            <!-- Botão Download -->
            <b-button
              variant="outline-success"
              class="btn-action-icon"
              @click="$emit('download', data.item)"
              title="Baixar Nota Fiscal"
            >
              <Icon name="icon-download" size="18" />
            </b-button>
          </div>
        </template>
      </b-table>
    </div>
  </div>
</template>

<script>
import VueMonthlyPicker from "vue-monthly-picker";
import moment from "moment";
import Icon from "../../../../components/icon/Icon.vue";

export default {
  name: "HistoryInvoicesTab",
  components: {
    VueMonthlyPicker,
    Icon,
  },
  props: {
    items: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      selectedCompetence: "",
      invoiceSearch: "",
      maxDate: moment(),
      monthLabels: [
        "JAN", "FEV", "MAR", "ABR", "MAI", "JUN",
        "JUL", "AGO", "SET", "OUT", "NOV", "DEZ"
      ],
      fields: [
        { key: "competence", label: "Competência" },
        { key: "invoiceNumber", label: "Número da Nota" },
        { key: "integrationDate", label: "Data Integração" },
        { key: "actions", label: "", class: "text-right" },
      ],
    };
  },
  computed: {
    filteredItems() {
      const formattedSelectedMonth = this.selectedCompetence
        ? moment(this.selectedCompetence).format("MM/YYYY")
        : "";

      if (!this.invoiceSearch && !formattedSelectedMonth) {
        return this.items;
      }

      return this.items.filter((item) => {
        const matchesSearch = item.invoiceNumber
          .toLowerCase()
          .includes(this.invoiceSearch.toLowerCase());

        const matchesCompetence =
          !formattedSelectedMonth || item.competence === formattedSelectedMonth;

        return matchesSearch && matchesCompetence;
      });
    },
  },
  methods: {
    onFilterChange(date) {
      this.$emit("filter-change", {
        competence: date ? moment(date).format("MM/YYYY") : "",
        search: this.invoiceSearch,
      });
    },
    onSearchInput() {
      this.$emit("search", this.invoiceSearch);
    },
  },
};
</script>

<style lang="scss" scoped>

  .filters-container {
  display: flex;
  flex-direction: row;
  align-items: flex-end; /* Garante alinhamento exato pela base */
  gap: 16px;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
  }

  .form-select-group {
    display: flex;
    flex-direction: column;
    width: 162px;

    @media (max-width: 768px) {
      width: 100%;
    }

    .select-label {
      font-size: 16px;
      line-height: 24px;
      color: #212529;
      margin-bottom: 8px;
    }

    .custom-monthly-picker {
      width: 100%;

      ::v-deep .month-picker-wrapper {
        .input-uc {
          height: 29px !important;
          max-height: 29px !important;
          padding: 4px 8px !important;
          background: #ffffff !important;
          border-radius: 4px !important;
          font-size: 14px !important;
          color: #393c40 !important;
          box-shadow: none !important;
          display: flex;
          align-items: center;
        }

        .date-popover {
          z-index: 100 !important;
        }
      }
    }
  }

  .form-search-group {
    flex: 1;

    /* Nivelamento total do input de busca com o picker de data */
    .custom-search-group {
      max-height: 38px !important;

      ::v-deep .input-group-text,
      ::v-deep .search-icon-prepend {
        height: 38px !important;
        background-color: #ffffff !important;
        border-right: none !important;
        border-radius: 4px 0 0 4px !important;
        display: flex;
        align-items: center;

        .search-icon {
          font-size: 13px;
          color: #6c757d;
        }
      }

      ::v-deep .custom-search-input {
        height: 38px !important;
        max-height: 38px !important;
        border: 1px solid #ced4da !important;
        border-left: none !important;
        border-radius: 0 4px 4px 0 !important;
        font-size: 14px !important;
        color: #393c40 !important;
        box-shadow: none !important;
        text-align: left !important;
        padding: 0 12px 0 0 !important;

        &::placeholder {
          color: #6c757d;
          text-align: left;
        }

        &:focus {
          border-color: #ced4da !important;
        }
      }
    }
  }
}

.history-table-wrapper {
  .custom-history-table {
    ::v-deep th {
      background-color: #f6f7f7 !important;
      color: #212529;
      font-weight: 700;
      font-size: 14px;
      border-bottom: 1px solid #ccd4d6;
      padding: 16px 12px;
    }

    ::v-deep td {
      border-bottom: 1px solid #dfdfdf;
      padding: 12px;
      vertical-align: middle;
      height: 56px;
    }

    .cell-text {
      font-size: 14px;
      color: #1d1b20;
    }

    .btn-action-icon {
      width: 37px;
      height: 29px;
      border: 1px solid #007a4a;
      border-radius: 4px;
      color: #007a4a;
      padding: 0;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      background: transparent;

      &:hover {
        background-color: #007a4a;
        color: #ffffff;
      }
    }

    .btn-delete-active {
      width: 37px;
      height: 29px;
      border: 1px solid #b22222;
      border-radius: 4px;
      color: #b22222;
      padding: 0;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      background: transparent;

      &:hover {
        background-color: #b22222;
        color: #ffffff;
      }
    }

    .btn-delete-disabled {
      width: 37px;
      height: 29px;
      border: 1px solid #ced4da;
      border-radius: 4px;
      color: #6c757d;
      padding: 0;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      background: #f4f6f6;
      cursor: not-allowed;
    }
  }
}
</style>